import React,{useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArrowRight,FiCheck}=FiIcons;

const questions=[
  {text: "Purpose Alignment — Is the book's spiritual or thematic purpose clear?",weight: 2},
  {text: "Message Clarity — Does each chapter communicate the core message clearly?",weight: 2},
  {text: "Structure & Flow — Does the narrative or argument progress logically?",weight: 2},
  {text: "Voice & Tone — Is the author's voice authentic, consistent, and engaging?",weight: 1},
  {text: "Reader Connection — Will the target reader feel seen and helped?",weight: 1},
  {text: "Faith Integration — Is Scripture or Christian worldview woven naturally?",weight: 2},
  {text: "Pacing & Transitions — Do sections move smoothly without dragging?",weight: 1},
  {text: "Style & Readability — Are sentences clear and polished without jargon?",weight: 1},
  {text: "Consistency & Continuity — Are details, POV, tense, and facts consistent?",weight: 1},
  {text: "Completeness — Are all chapters or elements fully drafted?",weight: 2},
  {text: "Emotional/Spiritual Impact — Does the story or message evoke reflection, conviction, or hope?",weight: 1},
  {text: "Feedback Incorporation — Have others read and given input that shaped improvements?",weight: 1},
];

const ScorcardForm=({onComplete})=> {
  const [responses,setResponses]=useState({});
  const [currentQuestion,setCurrentQuestion]=useState(0);

  const handleResponse=(questionIndex,value)=> {
    setResponses((prev)=> ({...prev,[questionIndex]: value}));
  };

  const handleNext=()=> {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious=()=> {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit=()=> {
    const maxScore=questions.reduce((sum,q)=> sum + (5 * q.weight),0);
    const totalScore=questions.reduce((sum,q,index)=> {
      const score=responses[index] || 0;
      return sum + (score * q.weight);
    },0);

    const scoredQuestions=questions.map((q,index)=> ({
      text: q.text.split('—')[0].trim(),
      score: responses[index] || 0,
      originalIndex: index,
    }));

    scoredQuestions.sort((a,b)=> b.score - a.score);
    const strengths=scoredQuestions.slice(0,2);
    const weaknesses=scoredQuestions.slice(-2).reverse();
    
    const riskFlags=[];
    if ((responses[0] || 0) < 3) riskFlags.push({text: "Purpose Alignment",score: responses[0]});
    if ((responses[5] || 0) < 3) riskFlags.push({text: "Faith Integration",score: responses[5]});
    if ((responses[2] || 0) < 3) riskFlags.push({text: "Structure & Flow",score: responses[2]});
    if ((responses[11] || 0) < 3) riskFlags.push({text: "Feedback Incorporation",score: responses[11]});

    onComplete({
      responses,
      totalScore,
      maxScore,
      strengths,
      weaknesses,
      riskFlags
    });
  };

  const isCurrentQuestionAnswered=responses[currentQuestion] !==undefined;
  const allQuestionsAnswered=Object.keys(responses).length===questions.length;
  const progress=((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-700">
              Question {currentQuestion + 1} of {questions.length}
            </h2>
            <span className="text-sm text-gray-500">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-primary h-2 rounded-full"
              initial={{width: 0}}
              animate={{width: `${progress}%`}}
              transition={{duration: 0.5}}
            />
          </div>
        </div>

        {/* Question Card */}
        <motion.div
          key={currentQuestion}
          initial={{opacity: 0,x: 20}}
          animate={{opacity: 1,x: 0}}
          exit={{opacity: 0,x: -20}}
          transition={{duration: 0.4}}
          className="bg-white rounded-2xl shadow-lg p-8 mb-8"
        >
          <h3 className="text-2xl font-serif text-gray-900 mb-8 leading-relaxed">
            {questions[currentQuestion].text}
          </h3>
          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-600 mb-4">
              Rate how true this statement is for your manuscript:
            </p>
            <div className="grid grid-cols-5 gap-3">
              {[1,2,3,4,5].map((value)=> (
                <motion.button
                  key={value}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  onClick={()=> handleResponse(currentQuestion,value)}
                  className={`
                    relative p-4 rounded-xl border-2 transition-all duration-300 text-center
                    ${responses[currentQuestion]===value
                      ? 'border-primary bg-primary text-white shadow-lg'
                      : 'border-gray-200 hover:border-primary/50 hover:bg-primary/5'
                    }
                  `}
                >
                  <div className="text-xl font-bold mb-1">{value}</div>
                  <div className="text-xs">
                    {value===1 && 'Needs Work'}
                    {value===3 && 'Average'}
                    {value===5 && 'Excellent'}
                  </div>
                  {responses[currentQuestion]===value && (
                    <motion.div
                      initial={{scale: 0}}
                      animate={{scale: 1}}
                      className="absolute -top-2 -right-2 bg-accent text-white rounded-full p-1"
                    >
                      <SafeIcon icon={FiCheck} className="text-sm" />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
        
        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion===0}
            className={`
              px-6 py-3 rounded-lg font-medium transition-all duration-300
              ${currentQuestion===0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:text-primary hover:bg-primary/5'
              }
            `}
          >
            Previous
          </button>
          
          {currentQuestion < questions.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={!isCurrentQuestionAnswered}
              className={`
                flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300
                ${isCurrentQuestionAnswered
                  ? 'bg-primary hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              Next Question
              <SafeIcon icon={FiArrowRight} className="text-lg" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered}
              className={`
                flex items-center gap-2 px-8 py-3 rounded-lg font-medium transition-all duration-300
                ${allQuestionsAnswered
                  ? 'bg-accent hover:bg-orange-600 text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              View Results
              <SafeIcon icon={FiCheck} className="text-lg" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScorcardForm;